import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import {
  type AtlasGqlAsset,
  type AtlasGqlInspection,
  type AtlasGqlInspectionTask,
  type AtlasGqlInternalBladeInspectionTask,
} from 'types/atlas-graphql';

import { CreateSingleInspectionForm } from '../../CreateSingleInspection/CreateSingleInspectionForm';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { type InspectionUploadWorkflowComponentProps, Steps } from '../UploadInspectionDataModal';

import { type ValidationProps } from './Validation';

type SingleInspectionProps = Omit<InspectionUploadWorkflowComponentProps, 'uploadId'> & {
  onClose: () => void;
  task?: (AtlasGqlInspectionTask | AtlasGqlInternalBladeInspectionTask) | null;
  asset?: AtlasGqlAsset | null;
  onSingleInspectionCreate?: () => void;
  setValidationStepProps: Dispatch<
    SetStateAction<Omit<ValidationProps, 'setStep' | 'setPendingStep' | 'rollback'>>
  >;
};

export const SingleInspection = ({
  step,
  onClose,
  setValidationStepProps,
  onSingleInspectionCreate,
  setStep,
  task,
  asset,
}: SingleInspectionProps) => {
  const { refetch } = useServerDataTableContext().context;
  const [hasNewInspectionTask, setHasNewInspectionTask] = useState<boolean>(false);

  const { setInspectionType, setCreatedInspectionIds, setCreatedTaskIds } =
    useUploadInspectionDataModalContext();
  const handleCreate = useCallback(
    async (inspection: AtlasGqlInspection | undefined) => {
      // conditionally refetch the inspection list
      refetch && (await refetch());

      if (onSingleInspectionCreate) {
        onSingleInspectionCreate();
      }

      /**
       * If creation was successful, proceed
       * If not, error toast will be shown (from child component), remain here to try again
       */
      if (inspection) {
        setValidationStepProps({
          inspection,
          hasNewInspectionTask,
        });
        setCreatedInspectionIds([inspection.id]);
        if (hasNewInspectionTask && inspection.taskId) {
          setCreatedTaskIds([inspection.taskId]);
        }
        if (inspection?.type) {
          setInspectionType(inspection.type);
        }
        setStep(Steps.SingleUploadValidation);
      }
    },
    [
      refetch,
      onSingleInspectionCreate,
      hasNewInspectionTask,
      setStep,
      setValidationStepProps,
      setInspectionType,
    ]
  );

  return (
    <>
      {step === Steps.SingleUpload && (
        <CreateSingleInspectionForm
          onClose={onClose}
          onCreate={handleCreate}
          setHasNewInspectionTask={setHasNewInspectionTask}
          task={task}
          asset={asset}
          saveButtonText="Next"
        />
      )}
    </>
  );
};
